// src/services/notificationService.js - Notification Service for Live Draft
/**
 * Centralized notification and sound service for live draft events
 * Handles browser notifications, sound effects, and visual alerts
 */

class NotificationService {
  constructor() {
    // Load preferences from localStorage
    this.soundsEnabled = localStorage.getItem('draft_sounds_enabled') !== 'false';
    this.notificationsEnabled = localStorage.getItem('draft_notifications_enabled') !== 'false';
    this.permission = 'default';
    
    // Check current permission status
    if ('Notification' in window) {
      this.permission = Notification.permission;
    }
  }
  
  /**
   * Request browser notification permission
   * @returns {Promise<boolean>} True if permission granted
   */
  async requestPermission() {
    if (!('Notification' in window)) {
      console.warn('Browser does not support notifications');
      return false;
    }
    
    if (Notification.permission === 'default') {
      try {
        this.permission = await Notification.requestPermission();
        return this.permission === 'granted';
      } catch (err) {
        console.error('Error requesting notification permission:', err);
        return false;
      }
    }
    
    return Notification.permission === 'granted';
  }
  
  /**
   * Show browser notification
   * @param {string} title - Notification title
   * @param {string} body - Notification body
   * @param {object} options - Notification options
   */
  showBrowserNotification(title, body, options = {}) {
    if (!this.notificationsEnabled) return null;
    
    if ('Notification' in window && Notification.permission === 'granted') {
      try {
        const notification = new Notification(title, {
          body,
          icon: options.icon || '/logo192.png',
          badge: options.badge || '/logo192.png',
          tag: options.tag || 'dynasty-draft',
          requireInteraction: options.requireInteraction || false,
          silent: options.silent || false
        });
        
        // Auto-close after 5 seconds unless requireInteraction
        if (!options.requireInteraction) {
          setTimeout(() => {
            notification.close();
          }, 5000);
        }
        
        return notification;
      } catch (err) {
        console.error('Error showing notification:', err);
        return null;
      }
    }
    
    return null;
  }
  
  /**
   * Play sound effect
   * @param {string} soundName - Name of sound file (without .mp3)
   */
  playSound(soundName) {
    if (!this.soundsEnabled) return;
    
    try {
      const audio = new Audio(`/sounds/${soundName}.mp3`);
      audio.volume = 0.5;
      audio.play().catch(err => {
        // User interaction may be required - fail silently
        console.debug('Sound play failed (may need user interaction):', err);
      });
    } catch (err) {
      console.error('Error playing sound:', err);
    }
  }
  
  /**
   * Notify when it's user's turn
   * @param {number} pickNumber - Current pick number
   */
  notifyYourTurn(pickNumber) {
    this.showBrowserNotification(
      'Your Turn!',
      `Pick #${pickNumber} is yours`,
      {
        requireInteraction: true, // Keep notification visible
        tag: 'your-turn'
      }
    );
    
    this.playSound('your-turn');
    
    // Flash screen with CSS class
    if (document.body) {
      document.body.classList.add('your-turn-pulse');
      setTimeout(() => {
        document.body.classList.remove('your-turn-pulse');
      }, 3000);
    }
  }
  
  /**
   * Notify when a pick is made
   * @param {string} teamName - Team that made the pick
   * @param {string} playerName - Player selected
   * @param {boolean} isYourPick - Whether it was the user's pick
   */
  notifyPickMade(teamName, playerName, isYourPick = false) {
    if (isYourPick) {
      this.showBrowserNotification(
        'Pick Made!',
        `You selected ${playerName}`,
        { tag: 'pick-made' }
      );
    } else {
      this.showBrowserNotification(
        'Pick Made',
        `${teamName} selected ${playerName}`,
        { tag: 'pick-made', silent: true }
      );
    }
    
    this.playSound('pick-made');
  }
  
  /**
   * Notify when timer is running low
   * @param {number} secondsRemaining - Seconds remaining on timer
   */
  notifyTimerWarning(secondsRemaining) {
    if (secondsRemaining <= 10 && secondsRemaining > 0) {
      this.playSound('timer-warning');
    }
  }
  
  /**
   * Notify when draft is complete
   */
  notifyDraftComplete() {
    this.showBrowserNotification(
      'Draft Complete!',
      'All picks have been made',
      { requireInteraction: true, tag: 'draft-complete' }
    );
    
    this.playSound('draft-complete');
  }
  
  /**
   * Enable/disable sounds
   * @param {boolean} enabled - Whether sounds should be enabled
   */
  setSoundsEnabled(enabled) {
    this.soundsEnabled = enabled;
    localStorage.setItem('draft_sounds_enabled', enabled);
  }
  
  /**
   * Enable/disable notifications
   * @param {boolean} enabled - Whether notifications should be enabled
   */
  setNotificationsEnabled(enabled) {
    this.notificationsEnabled = enabled;
    localStorage.setItem('draft_notifications_enabled', enabled);
  }
  
  /**
   * Get current sound setting
   * @returns {boolean} Whether sounds are enabled
   */
  getSoundsEnabled() {
    return this.soundsEnabled;
  }
  
  /**
   * Get current notification setting
   * @returns {boolean} Whether notifications are enabled
   */
  getNotificationsEnabled() {
    return this.notificationsEnabled;
  }
  
  /**
   * Get notification permission status
   * @returns {string} 'granted', 'denied', or 'default'
   */
  getPermissionStatus() {
    if ('Notification' in window) {
      return Notification.permission;
    }
    return 'unsupported';
  }
}

// Export singleton instance
export default new NotificationService();

